import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Divider } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { assignManifestToDriver, getManifestList, getOrderByManifestNo } from '../../../actions/driverPickupActions';
import { Details } from '@material-ui/icons';
import PickupListDetail from '../PickupListDetail';
import UniDialog from '../../Common/UniDialog';
import { showAlertSnackbar } from '../../../reducers/uiSlice';
import PickupSearch from '../PickupSearch';
import PickupList from '../PickupList';
import moment from 'moment';
import { getPartnerList } from "../../../actions/mmActions";

const useStyles = makeStyles((theme) => ({
  section: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  boxStyle: {
    height: 750
  }
}));

export default function FetchPickupLists(props) {
  const classes = useStyles();
  const { t } = useTranslation('driverPickup');
  const dispatch = useDispatch();
  const [partnerList, setPartnerList] = useState();

  const STATUS_LIST = {
    0: t('new_create'),
    1: t('picking_up'),
    2: t('partial_picked_up'),
    3: t('picked_up_done')
  }

  const columns = [
    { field: 'id', flex: 1, headerName: t('manifest_no'), width: 150, sortable: false, headerAlign: 'center', align: 'center' },
    {
      field: 'create_time',
      headerName: t('create_time'),
      headerAlign: 'center',
      width: 200,
      flex: 1,
      valueFormatter: (params) => params.value ? moment.unix(params.value).format("MMM DD, YYYY - HH:mm:ss") : '',
      align: 'center'
    },
    {
      field: 'status',
      headerName: t('status'),
      width: 150,
      flex: 1,
      headerAlign: 'center',
      valueFormatter: (params) => {
        return STATUS_LIST[params.value]
      },
      align: 'center'
    },
    { field: 'total_count', flex: 1, headerName: t('total_count'), width: 150, headerAlign: 'center', align: 'center' },
    {
      field: 'total_weight',
      flex: 1,
      headerName: t('total_weight'),
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <div>{params.value || 0}</div>
      )
    },
    {
      field: 'total_volume',
      flex: 1,
      headerName: t('total_volume'),
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <div>{params.value || 0}</div>
      )
    },
    {
      field: 'partner_id',
      flex: 1,
      headerName: t('partner_id'),
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <div>{displayPartnerName(params.value, partnerList)}</div>
      )
    },
    { field: 'creator', flex: 1, headerName: t('creator'), width: 150, sortable: false, headerAlign: 'center', align: 'center' },
    { field: 'pickup_driver', flex: 1, headerName: t('pickup_driver'), width: 150, sortable: false, headerAlign: 'center', align: 'center' },
    {
      field: 'actions',
      headerName: t('view_details'),
      sortable: false,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        return (
          <div>
            <IconButton
              id='manifestmanagement_iconbutton'
              size='small'
              onClick={(e) => {
                e.stopPropagation()
                handleViewDetails(params.row.id);
              }}
              disabled={!params.row.id}
            >
              <Details color='primary' />
            </IconButton>
          </div>
        )
      }
    }
  ];

  const [isDataLoading, setIsDataLoading] = useState(false);
  const [openViewDetails, setOpenViewDetails] = useState(false);
  const [manifestList, setManifestList] = useState([]);
  const [manifestDetails, setManifestDetails] = useState([]);
  const [driverId, setDriverId] = useState('');
  const [pickupWarehouse, setPickupWarehouse] = useState('');

  const _handleSearch = (searchType, fromDate, toDate, creator, partnerId, pickupDriver, warehouse) => {
    setIsDataLoading(true);
    setPickupWarehouse(warehouse);
    let params = {};
    if (searchType === 0) {
      params['fromTS'] = moment(fromDate).startOf('day').unix();
      params['toTS'] = moment(toDate).endOf('day').unix();
    }
    params['partner_id'] = partnerId;
    params['creator'] = creator;
    params['pickup_driver'] = pickupDriver;
    params['pickup_warehouse'] = warehouse;
    dispatch(getManifestList(params))
      .then((res) => {
        setManifestList(res);
        if (res.length === 0) {
          dispatch(showAlertSnackbar({ message: t('no_res'), type: 'warning' }))
        }
      })
      .catch(() => {
        dispatch(showAlertSnackbar({ message: t('fetch_failure'), type: 'error' }))
      })
      .finally(() => {
        setIsDataLoading(false);
      })
  }
  function handleViewDetails(manifestNo) {
    let params = {
      manifest_no: manifestNo
    }
    dispatch(getOrderByManifestNo(params))
      .then((res) => {
        setManifestDetails(res);
        if (res.length === 0) {
          dispatch(showAlertSnackbar({ message: t('no_res'), type: 'warning' }))
        } else {
          setOpenViewDetails(true);
          dispatch(showAlertSnackbar({ message: t('fetch_success'), type: 'success' }))
        }
      })
      .catch(() => {
        dispatch(showAlertSnackbar({ message: t('fetch_failure'), type: 'error' }))
      })
  }

  function displayPartnerName(id, partnerList) {
    const partner = partnerList.find(partner => partner.id === id);
    return partner ? partner.name : "";
  }

  function handleAssign(selected) {
    setIsDataLoading(true);
    let params = {
      manifest_lists: selected,
      pickup_driver: driverId
    }
    dispatch(assignManifestToDriver(params))
      .then((res) => {
        dispatch(showAlertSnackbar({ message: res, type: 'info' }))
      })
      .catch(() => {
        dispatch(showAlertSnackbar({ message: t('assign_fail'), type: 'info' }))
      })
      .finally(() => {
        setIsDataLoading(false);
      })
  }
  function createRows() {
    return manifestList.map((row) => {
      const { manifest_no, id, ...rest } = row
      return { id: manifest_no, temp: id, ...rest }
    })
  }

  useEffect(() => {
    dispatch(getPartnerList()).then((res) => {
      setPartnerList(res);
    })
      .catch(() => {
        setIsDataLoading(false);
      })

  }, []);

  useEffect(() => {
    if (props.open) {
      setManifestList([]);
    }
  }, [props.open])

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className={classes.section}>
        <PickupSearch
          onSearchClick={_handleSearch}
          type='fetch'
        />
      </div>
      <Divider />
      <div className={classes.section} style={{ flexGrow: 1 }}>
        <PickupList
          isDataLoading={isDataLoading}
          createRows={createRows || []}
          columns={columns}
          list={manifestList}
          setValue={setDriverId}
          handleSubmit={handleAssign}
          type={'fetch'}
          title={t('assign_driver')}
          dialogTitle={t('assign_to_driver')}
          dialogMessage={t('input_driver_id')}
          dialogPlaceholder={t('input_designated_driver')}
          pickupWarehouse={pickupWarehouse}
        />
      </div>
      <UniDialog
        open={openViewDetails}
        onClose={() => {
          setOpenViewDetails(false);
        }}
        title={t('view_details')}
        disableDefaultButtons
        maxWidth='lg'
        fullWidth
      >
        <PickupListDetail type='modal' manifestDetails={manifestDetails} />
      </UniDialog>
    </Box>
  );
}
